/**
 * Inline-editable animal group name, rendered inside the detail header.
 * Click the name (or pencil) to edit; Enter/blur saves, Escape cancels.
 * Replaces the old "edit animal group" route — the only editable field was
 * the name anyway.
 */

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { AnimalGroup } from "model";
import { useUpdateAnimalGroup } from "model";
import { useEffect, useRef, useState } from "react";

const NameInput = styled("input")({
  font: "inherit",
  color: "inherit",
  background: "transparent",
  border: "none",
  borderBottom: "2px solid #7D5DA3",
  outline: "none",
  padding: 0,
  margin: 0,
  maxWidth: "100%",
});

export default function EditableGroupName({
  animalGroup,
}: {
  animalGroup: AnimalGroup;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(animalGroup.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const updateMutation = useUpdateAnimalGroup();

  // Keep the local value in sync when not actively editing.
  useEffect(() => {
    if (!editing) setValue(animalGroup.name);
  }, [animalGroup.name, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function save() {
    setEditing(false);
    const trimmed = value.trim();
    if (!trimmed || trimmed === animalGroup.name) {
      setValue(animalGroup.name);
      return;
    }
    void updateMutation
      .mutateAsync({ ...animalGroup, name: trimmed })
      .catch(() => setValue(animalGroup.name));
  }

  if (editing) {
    return (
      <NameInput
        ref={inputRef}
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") {
            setValue(animalGroup.name);
            setEditing(false);
          }
        }}
        style={{ width: `${Math.max(value.length, 4) + 1}ch` }}
      />
    );
  }

  return (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") setEditing(true);
      }}
      sx={{
        cursor: "text",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        borderRadius: 1,
        outline: "none",
        "&:hover .editPencil": { opacity: 1 },
      }}
    >
      {animalGroup.name}
      <EditOutlinedIcon
        className="editPencil"
        sx={{
          fontSize: 16,
          color: "#7D5DA3",
          opacity: 0.5,
          transition: "opacity 0.15s",
          flexShrink: 0,
        }}
      />
    </Box>
  );
}
